import { all, put, takeEvery, call } from 'redux-saga/effects';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import { request } from '../../common/util/RestClient';
import envConfig from '../../config';

import {
  bookmarkFiles,
  deleteFiles,
  downloadFile,
  editFile,
  fetchFiles,
  fetchFilesTimeline,
  pinFile,
  refreshFiles,
  removeFile,
  removeScopedFile,
  uploadFile,
  uploadFileMetaData,
  uploadScopedFile,
} from '../routines';
import { createEntityLevelMap } from '../../common/util/successPlanUtils';
import { SUCCESS_PLAN_LEVEL } from '../../common/constants/SuccessPlanConstants';
import {
  fetchSuccessPlanFiles,
  fetchSuccessPlanFilesV2,
} from '../routines/successPlanRoutines';
import { DEFAULT_FILES_PAGINATION } from '../../common/constants/FileUploadConstants';

/**
 * deprecated, use uploadScopedFileSaga instead
 */
export function* uploadFileToS3WithPreSignedUrl(file) {
  const requestUrl = `${envConfig.APIG_URL}file-management/files/get-upload-url?fileName=${file.name}`;
  const response = yield call(request, requestUrl);
  const preSignedUrl = response.data.url;

  const requestOptions = {
    method: 'PUT',
    body: file,
  };
  yield fetch(preSignedUrl, requestOptions);

  return response.data;
}

export function* saveUploadedFile(fileDate) {
  try {
    const { name, source, key } = fileDate;

    const body = {
      name,
      source,
      key,
    };

    const config = {
      method: 'POST',
      payload: body,
    };

    const response = yield call(
      request,
      `${envConfig.APIG_URL}file-management/files/save-upload`,
      config
    );

    return response.data[0];
  } catch (error) {
    yield put(uploadFile.failure({ error }));
    return null;
  }
}

export function* fileUploadSaga({ payload }) {
  const { file, source = 'system', onSuccess, ...additionalData } = payload;
  const { uid } = file;
  try {
    const s3Response = yield uploadFileToS3WithPreSignedUrl(file);
    const fileData = yield saveUploadedFile({ ...s3Response, source });

    const data = { ...fileData, ...additionalData };
    data.uid = uid;
    yield put(uploadFile.success({ data }));
    if (onSuccess) onSuccess(data);
  } catch (error) {
    yield put(uploadFile.failure({ error }));
  }
}

export function* uploadScopedFileSaga({ payload }) {
  const {
    file,
    scope,
    source = 'system',
    onSuccess,
    ...additionalData
  } = payload;
  const { uid } = file;
  try {
    const s3Response = yield uploadFileToS3WithPreSignedUrl(file);
    const fileData = yield saveUploadedFile({ ...s3Response, source });

    const data = { ...fileData, ...additionalData };
    data.uid = uid;

    yield put(
      uploadScopedFile.success({
        scope,
        data: {
          ...data,
          ...additionalData,
          uid,
        },
      })
    );
    if (onSuccess) onSuccess(data);
  } catch (error) {
    yield put(uploadScopedFile.failure({ error }));
  }
}

export function* removeFileSaga({ payload }) {
  const { fileMetaDataId, clearAll } = payload;
  try {
    yield put(removeFile.success({ fileMetaDataId, clearAll }));
  } catch (error) {
    yield put(removeFile.failure({ error }));
  }
}

export function* removeScopedFileSaga({ payload }) {
  const { fileMetaDataId, scope, clearAll } = payload;
  try {
    yield put(removeScopedFile.success({ fileMetaDataId, scope, clearAll }));
  } catch (error) {
    yield put(removeScopedFile.failure({ error }));
  }
}

export function* editFileSaga({ payload }) {
  const { comment, fileGroupId, level, entityId, onSuccess } = payload;
  try {
    const body = {
      comment: comment.note,
    };

    const URL = `${envConfig.APIG_URL}core/files/${fileGroupId}`;
    const config = {
      method: 'POST',
      payload: body,
    };
    const response = yield call(request, URL, config);
    yield put(
      uploadFileMetaData.success({
        response,
      })
    );
    if (level === SUCCESS_PLAN_LEVEL) {
      // this should remove once success plan files v2 is fully implemented
      yield put(
        fetchSuccessPlanFiles.trigger({
          level,
          entityId,
        })
      );
      yield put(
        fetchSuccessPlanFilesV2.trigger({
          level,
          entityId,
        })
      );
    } else {
      yield put(
        fetchFiles.trigger({
          level,
          entityId,
          paginationParams: DEFAULT_FILES_PAGINATION,
        })
      );
    }
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(uploadFileMetaData.failure({ error }));
  }
}

export function* fileMetaDataSaga({ payload }) {
  const { files, comment, level, entityId, successPlanId, onSuccess } = payload;
  const isAdditionalLevel = successPlanId && successPlanId > 0;
  try {
    const metaData = files.map((item) => {
      delete item.uid;
      return item;
    });
    const URL = `${envConfig.APIG_URL}core/files`;
    const linkedEntities = {};

    const levels = [level];
    const entityIds = [entityId];
    if (isAdditionalLevel) {
      levels.push(...[SUCCESS_PLAN_LEVEL]);
      entityIds.push(...[successPlanId]);
    }
    const linked = createEntityLevelMap(levels, entityIds);
    linkedEntities[`${level}s`] = [entityId];
    if (successPlanId) {
      linkedEntities.success_plans = [successPlanId];
    }
    const body = {
      files: metaData,
      comment: comment.note,
      linkedEntities: linked,
    };

    const config = {
      method: 'POST',
      payload: body,
    };
    const response = yield call(request, URL, config);

    yield put(
      uploadFileMetaData.success({
        response,
        successMessage: SUCCESS_MESSAGES.UPLOAD_FILE,
      })
    );
    if (isAdditionalLevel) {
      yield put(
        fetchSuccessPlanFiles.trigger({
          level: SUCCESS_PLAN_LEVEL,
          entityId: successPlanId,
        })
      );
    } else {
      yield put(refreshFiles.trigger(Math.random()));
    }
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(uploadFileMetaData.failure({ error }));
  }
}

export function* downloadFileSaga({ payload }) {
  try {
    const URL = `${envConfig.APIG_URL}file-management/files/download/${payload}`;
    const response = yield call(request, URL, { method: 'POST' });
    const { url } = response.data;

    window.open(url, '_self', 'noopener');
    yield put(
      downloadFile.success({
        url,
      })
    );
  } catch (error) {
    yield put(downloadFile.failure({ error }));
  }
}

export function* bookmarkFilesSaga({ payload }) {
  const { formData, fileGroupId, entityId, level } = payload;
  try {
    const URL = `${envConfig.APIG_URL}core/files/${fileGroupId}/bookmark`;
    const body = {
      bookmarkStatus: formData.bookmarked,
    };
    const config = {
      method: 'PATCH',
      payload: body,
    };
    const response = yield call(request, URL, config);

    yield put(bookmarkFiles.success({ response }));
    if (level === SUCCESS_PLAN_LEVEL) {
      yield put(
        fetchSuccessPlanFiles.trigger({
          level,
          entityId,
        })
      );
    } else {
      yield put(
        fetchFiles.trigger({
          level,
          entityId,
          paginationParams: DEFAULT_FILES_PAGINATION,
        })
      );
    }
  } catch (error) {
    yield put(bookmarkFiles.failure({ error }));
  }
}

export function* deleteFilesSaga({ payload }) {
  try {
    const { level, entityId, fileGroupId } = payload;
    const URL = `${envConfig.APIG_URL}core/files/${fileGroupId}`;
    const response = yield call(request, URL, {
      method: 'DELETE',
    });
    yield put(
      deleteFiles.success({
        response,
        successMessage: SUCCESS_MESSAGES.DELETE_FILE,
      })
    );
    if (level === SUCCESS_PLAN_LEVEL) {
      // this should remove once success plan files v2 is fully implemented
      yield put(
        fetchSuccessPlanFiles.trigger({
          level,
          entityId,
        })
      );
      yield put(
        fetchSuccessPlanFilesV2.trigger({
          level,
          entityId,
        })
      );
    } else {
      yield put(
        fetchFiles.trigger({
          level,
          entityId,
          paginationParams: DEFAULT_FILES_PAGINATION,
        })
      );
    }
  } catch (error) {
    yield put(deleteFiles.failure({ error }));
  }
}

export function* pinFileSaga({ payload }) {
  const { pinStatus, fileId, entityId, level } = payload;
  try {
    const URL = `${envConfig.APIG_URL}core/files/${fileId}/pin`;
    const body = {
      pinStatus,
    };
    const config = {
      method: 'PATCH',
      payload: body,
    };
    const response = yield call(request, URL, config);

    yield put(pinFile.success({ response }));
    yield put(fetchFilesTimeline.trigger({ level, entityId }));
  } catch (error) {
    yield put(pinFile.failure({ error }));
  }
}

export default function* root() {
  yield all([takeEvery(uploadFile.TRIGGER, fileUploadSaga)]);
  yield all([takeEvery(uploadScopedFile.TRIGGER, uploadScopedFileSaga)]);
  yield all([takeEvery(uploadFileMetaData.TRIGGER, fileMetaDataSaga)]);
  yield all([takeEvery(removeFile.TRIGGER, removeFileSaga)]);
  yield all([takeEvery(removeScopedFile.TRIGGER, removeScopedFileSaga)]);
  yield all([takeEvery(downloadFile.TRIGGER, downloadFileSaga)]);
  yield all([takeEvery(editFile.TRIGGER, editFileSaga)]);
  yield all([takeEvery(bookmarkFiles.TRIGGER, bookmarkFilesSaga)]);
  yield all([takeEvery(deleteFiles.TRIGGER, deleteFilesSaga)]);
  yield all([takeEvery(pinFile.TRIGGER, pinFileSaga)]);
}
